import { NgClass } from "@angular/common";
import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart } from "chart.js";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../../../shared/components/timeout/timeout.component";
import { first } from "rxjs";
import { BotevoDesignTokens } from "../../../../../../../assets/themes/botevoPreset";

@Component({
  standalone: true,
  selector: "app-available-gates-doughnut-chart",
  imports: [TranslateModule, NgClass, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./available-gates-doughnut-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./available-gates-doughnut-chart.component.scss",
})
export class AvailableGatesDoughnutChartComponent {
  public chartId = input<string>();
  public totalCountState = input<number | null>();
  public availableCountState = input<number | null>();

  public loading = computed(() => {
    return this.totalCountState() === null || this.availableCountState() === null;
  });
  public timeOut = createTimeoutSignal(this.loading);
  private chart!: Chart<"doughnut">;
  private translate = inject(TranslateService);

  public percantage = computed(() => {
    const totalCount = this.totalCountState();
    const availableCount = this.availableCountState();

    if (totalCount !== null && totalCount !== undefined && availableCount !== null && availableCount !== undefined) {
      return Math.round((availableCount / totalCount) * 100) + "%";
    } else {
      return "";
    }
  });

  constructor() {
    effect(() => {
      const totalCount = this.totalCountState();
      const availableCount = this.availableCountState();
      if (totalCount !== null && totalCount !== undefined && availableCount !== null && availableCount !== undefined) {
        if (!this.chart) {
          this.initChart();
        }
        this.chart.data.datasets[0].data = [availableCount, totalCount - availableCount];
        this.chart.update();
      }
    });
  }
  /**
   * Initializes the doughnut chart.
   * Called when the component is initialized, or when the total count or available count changes.
   */
  private initChart() {
    const chartData = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [BotevoDesignTokens.primitive?.green?.[400], BotevoDesignTokens.primitive?.red?.[500]],
        },
      ],
    };
    const chartId = this.chartId();
    if (!chartId) {
      return;
    }
    this.chart = new Chart(chartId, {
      type: "doughnut",
      data: chartData,
      options: {
        responsive: true,
        cutout: 99,
        plugins: {
          legend: {
            position: "top",
            display: true,
            labels: {
              color: "#ffffff",
            },
          },
        },
      },
    });
    this.setLabels();
    this.translate.onLangChange.subscribe(() => {
      this.setLabels();
    });
  }

  /**
   * Updates the doughnut chart by translating the labels for available and not available gates,
   * and updates the chart.
   */
  private setLabels() {
    this.translate
      .get(["available_gates_doughnut_chart.available", "available_gates_doughnut_chart.not_available"])
      .pipe(first())
      .subscribe((translations) => {
        this.chart.data.labels = [
          translations["available_gates_doughnut_chart.available"],
          translations["available_gates_doughnut_chart.not_available"],
        ];
        this.chart.update();
      });
  }
}
